import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { enToSw, swToEn } from "./translations";

const LanguageContext = createContext(null);

const STORAGE_KEY = "tarewu_lang";

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === "sw" ? "sw" : "en";
    } catch (e) {
      return "en";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch (e) {
      // ignore (private browsing etc.)
    }
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang) => setLanguageState(lang === "sw" ? "sw" : "en");
  const toggleLanguage = () =>
    setLanguageState((prev) => (prev === "en" ? "sw" : "en"));

  // t() works regardless of which language the source string was written in:
  // it looks the text up in whichever map matches, and falls through to the
  // original text untranslated if there's no entry yet.
  const t = useMemo(() => {
    return (text) => {
      if (!text) return text;
      // Works no matter which language the source string was authored in:
      // look it up in whichever map matches the target language, otherwise
      // fall through to the original text unchanged.
      if (language === "sw") return enToSw[text] || text;
      return swToEn[text] || text;
    };
  }, [language]);

  const value = useMemo(
    () => ({ language, setLanguage, toggleLanguage, t }),
    [language, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
